import { green } from "@mui/material/colors";
import { createTheme, Palette } from "@mui/material/styles";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  // PRIMARY_MAIN_COLOR,
  // PAPER_MAIN_COLOR,
  DEFAULT_COLOR,
  COLORS,
  FONT_SIZES,
} from "./themeConstants";
import type {} from "@mui/x-data-grid/themeAugmentation";

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    actions: Palette["primary"];
  }
  interface PaletteOptions {
    actions: PaletteOptions["primary"];
  }
  interface PaletteColor {
    cancel?: string;
  }
  interface SimplePaletteColorOptions {
    cancel?: string;
  }
}

const appTheme = createTheme({
  palette: {
    background: {
      paper: COLORS.black_gradients.greyLight,
      default: DEFAULT_COLOR,
    },
    primary: {
      dark: "blue",
      main: COLORS.blue_gradients.blue, //"#0F64D5"
      light: COLORS.blue_gradients.light, //"#83B8FF"
      contrastText: DEFAULT_COLOR,
    },
    secondary: {
      dark: COLORS.black_gradients.black,
      main: COLORS.black_gradients.greyTwo,
      light: COLORS.black_gradients.greyOne,
    },
    error: {
      main: COLORS.red_gradients.red,
      light: COLORS.red_gradients.redLight,
    },
    success: {
      dark: COLORS.green_gradients.greenExcel,
      main: green[800],
      light: COLORS.green_gradients.green,
    },
    actions: {
      main: COLORS.purple_gradients.purple,
      cancel: COLORS.purple_gradients.purple,
    },
    text: {
      primary: COLORS.black_gradients.black,
      secondary: COLORS.black_gradients.greyOne,
      disabled: COLORS.black_gradients.greyTwo,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        fontFamily: "Inter",
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          "& >.MuiPaper-root": {
            backgroundColor: DEFAULT_COLOR,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.MuiAlert-root, &.MuiDialog-paper": {
            // backgroundColor: COLORS.black_gradients.greyLights
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // indicator: {
        //   width: "48px"
        // }
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZES.sizeThree.text,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          "&.Mui-disabled:before": {
            border: "none",
          },
          "&:after": {
            border: "none",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZES.sizeThree.text,
          borderRadius: 12,
        },
        // input:{
        //   "&.Mui-disabled":{
        //     "WebkitTextFillColor" : COLORS.black_gradients.black
        //   },
        // }
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          minWidth: "unset",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: FONT_SIZES.sizeThree.text,
          width: "40px",
          height: "40px",
          padding: 0,
          "&.Mui-disabled": {
            color: COLORS.black_gradients.greyOne,
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
        },
        footerContainer: {
          height: "45px",
          minHeight: "unset",
        },
        columnHeaders: {
          //header row
          backgroundColor: COLORS.black_gradients.greyLight, //same as paper
          borderRadius: "12px ",
          border: "none",
          marginBottom: "15px",
          "& .MuiButtonBase-root": {
            //sorting and menu buttons
            width: "20px",
          },
        },
        columnSeparator: {
          display: "none",
        },
        columnHeader: {
          maxHeight: "100%",
          height: "100%",

          "&:focus-within": {
            outline: "none",
          },

          "& .MuiTypography-root": {
            //when want to apply this style , wrap the the render content in Typography (useful for long titles)
            whiteSpace: "break-spaces",
            fontSize: FONT_SIZES.sizeTwo.text,
            textAlign: "center",
            color: COLORS.black_gradients.greyOne,
            fontWeight: 400,
            textOverflow: "ellipsis",
            overflow: "hidden",
            maxHeight: "100%",
          },
        },

        columnHeaderTitleContainer: {
          maxHeight: "100%",
          height: "100%",
          "& .MuiDataGrid-columnHeaderTitleContainerContent": {
            maxHeight: "100%",
            height: "100%",
          },
        },
        columnHeadersInner: {
          maxHeight: "100%",
          height: "100%",
        },
        columnHeaderTitle: {
          fontSize: FONT_SIZES.sizeTwo.text,
          color: COLORS.black_gradients.greyOne,
          fontWeight: 400,
          height: "100%",
          maxHeight: "100%",
        },

        cell: {
          //DataGrid cell
          fontSize: FONT_SIZES.sizeThree.text,
          textOverflow: "ellipsis",
          overflow: "hidden",
          // border:"none",
          "& .MuiTypography-root": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            cursor: "default",
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root": {
            borderRadius: 12,
            "& .MuiTableCell-root": {
              color: COLORS.black_gradients.greyOne,
              fontSize: FONT_SIZES.sizeTwo.text,
              "& p": {
                fontSize: FONT_SIZES.sizeTwo.text,
              },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZES.sizeThree.text,
          textOverflow: "ellipsis",
          overflow: "hidden",
          "& .MuiTypography-root": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            cursor: "default",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // root:{
        //   minWidth:"unset"
        // },
        endAdornment: {
          top: "calc(50% - 10px)",
          "& .MuiButtonBase-root": {
            width: "20px",
            height: "20px",
            borderRadius: "3px",
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: COLORS.black_gradients.greyLight,
        },
      },
    },
  },
  typography: {
    fontFamily: "Inter",
    h3: {
      fontSize: FONT_SIZES.sizeSeven.number,
    },
    h4: {
      fontSize: FONT_SIZES.sizeSix.number,
    },
    h5: {
      fontSize: FONT_SIZES.sizeFive.number,
    },
    h6: {
      fontSize: FONT_SIZES.sizeFour.number,
    },
    body1: {
      fontSize: FONT_SIZES.sizeThree.number,
    },
    body2: {
      fontSize: FONT_SIZES.sizeTwo.number,
    },
    subtitle2: {
      fontSize: FONT_SIZES.sizeOne.number,
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: [8, 5, 3],
});

const initialState = {
  palette: {
    background: appTheme.palette.background,
    primary: appTheme.palette.primary,
    secondary: appTheme.palette.secondary,
    error: appTheme.palette.error,
    success: appTheme.palette.success,
    text: appTheme.palette.text,
    actions: appTheme.palette.actions,
  } as Palette,
  components: appTheme.components,
  typography: appTheme.typography,
  shape: appTheme.shape,
};

export const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setPalette(state, action: PayloadAction<Palette>) {
      state.palette = action.payload;
    },
    resetPalette(state) {
      state.palette = appTheme.palette as Palette;
    },
  },
});

export default themeSlice.reducer;
